const extend = require('xtend/mutable');
const ajax = require('component-ajax');
const ejs = require('ejs');
const forEach = require('object-foreach');
const moment = require('moment');
const q = require('queried');

/**
 * Dynamic events list constructor
 * @param {Object} options
 * @constructor
 */
function EventsList(options) {
  extend(this, options);

  this.template = template;

  if (this.loadMoreButton) {
    this.loadMoreButton.addEventListener('click', this.load.bind(this));
  }
}

extend(EventsList.prototype, {

  ajax,
  templateDependencies: {},
  seenDates: [],
  loadedCount: 0,
  totalCount: 0,
  shownAlready: 0,
  itemsSelector: '.events-list-item',
  showItemsByStep: 10,

  /**
   * Load data from server and display it
   */
  load() {
    if (this.isRequireLoading()) {
      this.setPreloadingState(true);

      this.fetch((data) => {
        const html = this.render(data);
        this.appendHTML(html);

        this.loadedCount += data.events.length;
        this.shownAlready += Math.min(data.events.length, this.showItemsByStep);

        this.updateButtonState();
        this.setPreloadingState(false);
      });
    } else {
      const shownLength = this.showHiddenItems();
      this.shownAlready += shownLength;
      this.updateButtonState(this.showItemsByStep);
    }
  },

  /**
   * Get info from server
   * Wrap for ajax
   * @param  {Function} callback [description]
   */
  fetch(callback) {
    this.ajax({
      url: this.url,
      success: callback,
      data: {
        place_id: this.place_id,
        exclude: q.all('.events-list-item').map((item) => item.getAttribute('data-date-id')).join(),
      },
    });
  },

  /**
   * Check does events list requires fetching data from server
   * @return {Boolean}
   */
  isRequireLoading() {
    return this.loadedCount <= this.shownAlready;
  },

  /**
   * Make HTML using items default template
   * and passed data
   * @param  {Object} data
   * @return {String} - string of HTML
   */
  render(data) {
    extend(data, {
      forEach,
      moment,
      imagesAllowed: !document.body.classList.contains('body--noImages'),
      showByStep: this.showItemsByStep,
      getFormattedMonth: (date) => date.format('DD MMMM').replace(/\d\d\s/, '').toLowerCase(),
    }, this.templateDependencies);
    return ejs.render(this.template, data);
  },

  /**
   * Append rendered HTML to the container
   * @param  {String} html - rendered html
   */
  appendHTML(html) {
    const tbody = q('tbody', this.container);
    tbody.innerHTML += html;
  },

  /**
   * Set preloading state to true or false
   * @param {Boolean} bool
   */
  setPreloadingState(bool) {
    if (bool) {
      this.loadMoreButton.classList.add('preloading');
    } else {
      this.loadMoreButton.classList.remove('preloading');
    }
  },

  /**
   * Set correct button state according to number of events
   * shown, loaded and in total
   */
  updateButtonState() {
    if (
      this.loadedCount >= this.totalCount
      && this.shownAlready >= this.totalCount
    ) {
      this.hideLoadMoreButton();
    }
  },

  /**
   * Hide item's preloading button
   * Needed when all the events are loaded
   */
  hideLoadMoreButton() {
    this.loadMoreButton.setAttribute('hidden', 'hidden');
  },

  /**
   * Show items that already loaded but not shown yet
   * @return {[Number]} - number of items shown by the call
   */
  showHiddenItems() {
    const items = q.all(this.itemsSelector, this.container);
    let iters = 0;
    const threshold = Math.min(this.shownAlready + this.showItemsByStep, this.loadedCount);

    for (let i = this.shownAlready; i < threshold; i++) {
      items[i].removeAttribute('hidden');
      iters++;
    }
    return iters;
  },

});

const template = `
  <% var now = moment() %>
  <% forEach(events, function(item, index) { %>
    <% var event_date_id = item.event_date_id %> 
    <tr data-date-id= <%= event_date_id %> class="events-list-item"<% if (index >= showByStep) {%> hidden<% } %>>

      <td class="events-list-item-date">
        <% var startDate = item.start_date ? moment(item.start_date) : null %>
        <% var startTime = item.start_time ? moment(item.start_time, 'H:mm:ss') : null %>
        <% var endDate = item.end_date ? moment(item.end_date) : null %>
        <% var endTime = item.end_time ? moment(item.end_time, 'H:mm:ss') : null %>

        <% if (item.is_endless && item.is_startless) { %>
          <%= _("Permanently") %>

        <% } else if (item.is_endless) { %>
          <time class="date-squared events-list-item-date-inner">
            <span class="date-squared-dom">
              <%= startDate.format('D') %>
            </span><br/>
            <span class="date-squared-month"><%= getFormattedMonth(startDate) %></span><br/>
            <% if (!startDate.isSame(now, 'year')) { %>
              <span class="date-squared-year"><%= startDate.format('YYYY') %></span>
            <% } %>
          </time>
          <span class="events-list-item-date-divider">–</span>
          <span class="events-list-item-date-infinity">&infin;</span>

        <% } else if (item.is_startless) { %>
          <span class="events-list-item-date-infinity">&infin;</span>
          <span class="events-list-item-date-divider">–</span>
          <time class="date-squared events-list-item-date-inner">
            <span class="date-squared-dom">
              <%= endDate.format('D') %>
            </span><br/>
            <span class="date-squared-month"><%= getFormattedMonth(endDate) %></span><br/>
            <% if (!endDate.isSame(now, 'year')) { %>
              <span class="date-squared-year"><%= endDate.format('YYYY') %></span>
            <% } %>
          </time>

        <% } else if (!endDate || startDate.isSame(endDate, 'day')) { %>
          <time class="date-squared events-list-item-date-inner">
            <span class="date-squared-dom">
              <%= startDate.format('D') %>
            </span><br/>
            <span class="date-squared-month"><%= getFormattedMonth(startDate) %></span><br/>
            <% if (!startDate.isSame(now, 'year')) { %>
              <span class="date-squared-year"><%= startDate.format('YYYY') %></span>
            <% } %>
          </time>

        <% } else if (startDate.isSame(endDate, 'month')) { %>
          <time class="date-squared events-list-item-date-inner">
            <span class="date-squared-dom">
              <%= startDate.format('D') %>&ndash;<%= endDate.format('D') %>
            </span><br/>
            <span class="date-squared-month"><%= getFormattedMonth(startDate) %></span><br/>
            <% if (!startDate.isSame(now, 'year')) { %>
              <span class="date-squared-year"><%= startDate.format('YYYY') %></span>
            <% } %>
          </time>

        <% } else { %>
          <time class="date-squared events-list-item-date-inner">
            <span class="date-squared-dom">
              <%= startDate.format('D') %>
            </span><br/>
            <span class="date-squared-month"><%= getFormattedMonth(startDate) %></span><br/>
            <% if (startDate.year() != now.year() || endDate.year() != now.year()) { %>
              <span class="date-squared-year"><%= startDate.format('YYYY') %></span>
            <% } %>
          </time>

          <span class="events-list-item-date-divider">&ndash;</span>

          <time class="date-squared events-list-item-date-inner">
            <span class="date-squared-dom">
              <%= endDate.format('D') %>
            </span><br/>
            <span class="date-squared-month"><%= getFormattedMonth(endDate) %></span><br/>
            <% if (startDate.year() != now.year() || endDate.year() != now.year()) { %>
              <span class="date-squared-year"><%= endDate.format('YYYY') %></span>
            <% } %>
          </time>

        <% } %>
      </td>

      <% if (imagesAllowed) { %>
        <td class="events-list-item-image-cell">
          <a href="<%= item.site_url %>" class="events-list-item-preview">
            <% if (item.thumbs.xs) { %>
            <img src="<%= item.thumbs.xs %>" class="events-list-item-image">
            <% } %>
          </a>
        </td>
      <% } %>

      <td class="events-list-item-content">
        <header class="events-list-item-header">
          <h4 class="events-list-item-title">
            <a href="<%= item.site_url %>" title="<%= item.title %>" class="events-list-item-link">
              <span>
                <%= item.short_title || item.title %>
              </span>
            </a>
          </h4>
          <% if (item.tagline) { %>
          <div class="events-list-item-tagline"><%= item.tagline %></div>
          <% } %>
        </header>
      </td>

      <td class="events-list-item-details">
        <div class="events-list-item-price-details">
          <% if (item.price) { %>
            <p class="events-list-item-price"><%- roubleToSign(item.price) %></p>
          <% } else if (item.free) { %>
            <p class="events-list-item-price"><%= _("Free entrance") %></p>
          <% } %>
        </div>
      </td>

    </tr>
  <% }); %>
`;

module.exports = EventsList;
